import { HttpEvent, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, map, Observable } from 'rxjs';
import { ApiCallingV2Service } from './api-calling-v2.service';
import { API_CONFIG } from '../tokens/config.tokens';
import { ApiResponseOptions } from '../models/api-response.models';

const defaultSilent: ApiResponseOptions = {
  showLoader: true,
  showMessage: false,
  showError: false
};

/**
 * Same responsibilities as {@link FileUploadService} but uses {@link ApiCallingV2Service}
 * (loader finalize + optional toasts via v2 config).
 */
@Injectable({
  providedIn: 'root'
})
export class FileUploadServiceV2 {
  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  #toastr = inject(ToastrService);
  recording: boolean = false;

  #basUrlV2 = this.#apiConfig.baseUrlV2;

  private base(path: string): string {
    return `${this.#apiConfig.baseUrl}${path}`;
  }

  getAWSSignedUrl(file: File): Observable<{ fileName: string; presignedUrl: string; s3Key: string; sourceFile: File }> {
    const body = JSON.stringify(file.name);
    return this.#http.postData(this.base('documents/private/upload-url'), defaultSilent, body).pipe(
      map((data: any) => ({
        fileName: body,
        presignedUrl: data.uploadUrl,
        s3Key: data.s3Key,
        sourceFile: file
      })),
      catchError(error => this.handleError(error))
    );
  }

  getPrivateAWSSignedUrl(
    file: File
  ): Observable<{ fileName: string; presignedUrl: string; s3Key: string; sourceFile: File; documentId: string }> {
    const body = JSON.stringify(file.name);
    const config: ApiResponseOptions = {
      ...defaultSilent,
      options: { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
    };
    return this.#http.postData(this.base('documents/private/upload-url'), config, body).pipe(
      map((data: any) => ({
        fileName: body,
        presignedUrl: data.uploadUrl,
        s3Key: data.s3Key,
        documentId: data.documentId,
        sourceFile: file
      })),
      catchError(error => this.handleError(error))
    );
  }

  generatePublicUploadUrl(fileName?: string): Observable<{ documentId: string; s3Key: string; uploadUrl: string }> {
    const body = fileName !== undefined && fileName !== null ? JSON.stringify(fileName) : null;
    const config: ApiResponseOptions = {
      ...defaultSilent,
      options: { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
    };
    return this.#http.postData(this.base('documents/public/upload-url'), config, body).pipe(
      map((data: any) => {
        if (data && typeof data === 'object' && 'documentId' in data && 'uploadUrl' in data) {
          return data as { documentId: string; s3Key: string; uploadUrl: string };
        }
        throw new Error('Invalid public upload URL response');
      }),
      catchError(error => this.handleError(error))
    );
  }

  generatePrivateUploadUrl(fileName?: string): Observable<{ documentId: string; s3Key: string; uploadUrl: string }> {
    const body = fileName !== undefined && fileName !== null ? JSON.stringify(fileName) : null;
    const config: ApiResponseOptions = {
      ...defaultSilent,
      options: { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
    };
    return this.#http.postData(this.base('documents/private/upload-url'), config, body).pipe(
      map((data: any) => {
        if (data && typeof data === 'object' && 'documentId' in data && 'uploadUrl' in data) {
          return data as { documentId: string; s3Key: string; uploadUrl: string };
        }
        throw new Error('Invalid private upload URL response');
      }),
      catchError(error => this.handleError(error))
    );
  }

  getPresignedUrlFromS3Key(s3Key: string, fileName: string): Observable<string> {
    return this.#http
      .getData(
        this.base(
          `filesystem/file/presigned-url?s3Key=${encodeURIComponent(s3Key)}&fileName=${encodeURIComponent(fileName)}`
        ),
        defaultSilent
      )
      .pipe(
        map((data: any) => {
          const url = data?.presignedUrl;
          if (!url) throw new Error('Invalid presigned URL response');
          return url as string;
        }),
        catchError(error => this.handleError(error))
      );
  }

  getPresignedUrlFromS3DocumentId(s3DocumentId: string): Observable<string> {
    return this.#http.getData(`${this.#basUrlV2}/documents/${s3DocumentId}/presigned-url`, defaultSilent).pipe(
      map((url: string) => {
        if (!url) throw new Error('Invalid presigned URL response');
        return url;
      }),
      catchError(error => this.handleError(error))
    );
  }

  uploadFileToS3(fileObject: {
    fileName: string;
    presignedUrl: string;
    s3Key: string;
    sourceFile: File;
  }): Observable<{ fileName: string; presignedUrl: string; s3Key: string; sourceFile: File }> {
    const file = fileObject.sourceFile;
    return this.#http.awsFileUpload(fileObject.presignedUrl, file, defaultSilent);
  }

  uploadFileToS3WithProgress(fileObject: {
    fileName: string;
    presignedUrl: string;
    s3Key: string;
    sourceFile: File;
  }): Observable<HttpEvent<unknown>> {
    const file = fileObject.sourceFile;
    return this.#http.awsFileUpload(fileObject.presignedUrl, file, defaultSilent);
  }

  uploadFileToS3WithFetch(fileObject: {
    fileName: string;
    presignedUrl: string;
    s3Key: string;
    sourceFile: File;
  }): Observable<{ fileName: string; presignedUrl: string; s3Key: string; sourceFile: File }> {
    const file = fileObject.sourceFile;
    return this.#http.awsFileUploadWithFetch(fileObject.presignedUrl, file, defaultSilent).pipe(map(() => fileObject));
  }

  private handleError(error: any) {
    const errorMessage = error?.message || 'An error occurred';
    this.#toastr.error(errorMessage);
    return EMPTY;
  }
}
